/*
 **	OMNI_IQ (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **	Author(s): Oliver Sturrock olst@silverfallscapital.com
 **
 **/
import type { ReactNode } from 'react';
import type React from 'react';
import { Component } from 'react'
import { Alert } from './Alert'
import { Button } from './Button'
import { Card } from './Card'
import { theme } from '../../theme'
import { RotateCw, Bug } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  componentName?: string
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
  errorId?: string
}

export class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0
  private maxRetries = 3

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    const errorId = `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    return {
      hasError: true,
      error,
      errorId
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 ErrorBoundary caught an error:', {
      error,
      errorInfo,
      component: this.props.componentName,
      errorId: this.state.errorId,
      retryCount: this.retryCount,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    })

    this.props.onError?.(error, errorInfo)

    // Send to analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.message,
        fatal: false,
        custom_map: {
          error_id: this.state.errorId,
          component: this.props.componentName,
          retry_count: this.retryCount
        }
      })
    }
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++
      this.setState({
        hasError: false,
        error: undefined
      })
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { error, errorId } = this.state
      const componentName = this.props.componentName || 'Component'
      const canRetry = this.retryCount < this.maxRetries

      return (
        <Card
          style={{
            margin: theme.spacing.md,
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.borderRadius.md
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg, width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Bug size={48} style={{ color: theme.colors.error, marginBottom: theme.spacing.md }} />
              <h3 style={{ ...theme.typography.h3, color: theme.colors.error, margin: 0 }}>
                Something went wrong
              </h3>
              <span style={{ ...theme.typography.body, color: theme.colors.text.secondary }}>
                {componentName} encountered an unexpected error
              </span>
            </div>

            <Alert
              type="error"
              message="Error Details"
              description={error?.message || 'Unknown error occurred'}
              showIcon={true}
            />

            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', gap: theme.spacing.sm, justifyContent: 'center' }}>
                {canRetry && (
                  <Button
                    variant="primary"
                    onClick={this.handleRetry}
                  >
                    <RotateCw size={16} style={{ marginRight: theme.spacing.xs }} />
                    Try Again ({this.maxRetries - this.retryCount} attempts left)
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={this.handleReload}
                >
                  <RotateCw size={16} style={{ marginRight: theme.spacing.xs }} />
                  Reload Page
                </Button>
              </div>
            </div>

            <Alert
              type="info"
              message="If this problem persists, please contact support."
              style={{ marginTop: theme.spacing.md }}
            />
          </div>
        </Card>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary